/**
 * LearningTracker - Componente para tracking automático del aprendizaje
 * 
 * Este componente se encarga de:
 * - Tracking de tiempo de lectura (dwell time)
 * - Tracking de mensajes vistos
 * - Detectar cuando el usuario interactúa con respuestas
 * 
 * Se monta una sola vez en App.tsx y escucha los cambios del store.
 * 
 * NOTA: Si el usuario da feedback explícito (👍/👎), el dwell time no se registra
 * para evitar duplicados.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useLearningTracking } from '@/hooks/useLearningTracking';

export function LearningTracker() {
  const messages = useChatStore((s) => s.messages);
  const isLoading = useChatStore((s) => s.isLoading);
  const isMessageRated = useChatStore((s) => s.isMessageRated);
  const activeDwellMessageId = useChatStore((s) => s.activeDwellMessageId);
  const setActiveDwellMessage = useChatStore((s) => s.setActiveDwellMessage);
  
  const { 
    startDwellTracking, 
    endDwellTracking,
    resetDwellTracking,
    trackImplicitFeedback 
  } = useLearningTracking();

  // Refs para tracking
  const lastMessageCount = useRef<number>(0);
  const dwellTimeoutRef = useRef<any>(null);
  const currentQueryRef = useRef<string>('');
  const hasReportedDwell = useRef<boolean>(false);
  const currentMessageIdRef = useRef<string | null>(null);

  // Helper: Get last user query
  const getLastUserQuery = useCallback((): string => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        return messages[i].content;
      }
    }
    return '';
  }, [messages]);

  // Track when new AI message arrives
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    
    // New AI message arrived
    if (lastMessage.sender === 'ai' && messages.length > lastMessageCount.current) {
      const query = getLastUserQuery();
      
      if (query) {
        // End previous dwell tracking
        if (dwellTimeoutRef.current) {
          clearTimeout(dwellTimeoutRef.current);
          dwellTimeoutRef.current = null;
        }

        // Start new dwell tracking
        currentQueryRef.current = query;
        currentMessageIdRef.current = lastMessage.id;
        hasReportedDwell.current = false;
        
        // Register this message as the one being tracked in the store
        // This allows FeedbackButtons to cancel it
        setActiveDwellMessage(lastMessage.id);
        
        // Start tracking after a short delay (user needs time to see the message)
        setTimeout(() => {
          startDwellTracking(query);
        }, 500);

        // Report dwell after user has had time to read
        // We use a timeout to detect if they're still viewing
        dwellTimeoutRef.current = setTimeout(() => {
          if (!hasReportedDwell.current && currentMessageIdRef.current) {
            // Check if user already gave explicit feedback
            if (!isMessageRated(currentMessageIdRef.current)) {
              endDwellTracking(lastMessage.content.substring(0, 200));
            }
            hasReportedDwell.current = true;
            // Clear the active dwell message since we're done
            setActiveDwellMessage(null);
          }
        }, 15000); // Report after 15s of viewing
      }
    }

    lastMessageCount.current = messages.length;

    // Cleanup on unmount
    return () => {
      if (dwellTimeoutRef.current) {
        clearTimeout(dwellTimeoutRef.current);
      }
    };
  }, [messages, getLastUserQuery, startDwellTracking, endDwellTracking, isMessageRated, setActiveDwellMessage]);

  // Listen for dwell tracking cancellation (when user gives explicit feedback)
  useEffect(() => {
    // If activeDwellMessageId became null and we were tracking a message,
    // it means FeedbackButtons cancelled the tracking
    if (activeDwellMessageId === null && currentMessageIdRef.current && !hasReportedDwell.current) {
      // Cancel the timeout
      if (dwellTimeoutRef.current) {
        clearTimeout(dwellTimeoutRef.current);
        dwellTimeoutRef.current = null;
      }
      // Mark as reported so the follow-up effect doesn't also track
      hasReportedDwell.current = true;
      // IMPORTANT: Use resetDwellTracking instead of endDwellTracking!
      // The user gave explicit feedback (👍/👎), so we don't want ANY implicit tracking.
      // resetDwellTracking clears the refs without calculating/sending dwell time.
      resetDwellTracking();
      // Clear our local ref
      currentMessageIdRef.current = null;
    }
  }, [activeDwellMessageId, resetDwellTracking]);

  // Track when user sends a follow-up query (implicit follow-up)
  useEffect(() => {
    if (isLoading && currentQueryRef.current && !hasReportedDwell.current) {
      // User sent a new query while viewing previous response
      // This is an implicit follow-up
      trackImplicitFeedback(
        currentQueryRef.current,
        '',
        'implicit_follow_up',
        { follow_up_query: getLastUserQuery() }
      );
      
      // End current dwell tracking
      endDwellTracking();
      hasReportedDwell.current = true;
      
      // Clear the timeout and the active dwell message
      if (dwellTimeoutRef.current) {
        clearTimeout(dwellTimeoutRef.current);
        dwellTimeoutRef.current = null;
      }
      setActiveDwellMessage(null);
      currentMessageIdRef.current = null;
    }
  }, [isLoading, trackImplicitFeedback, endDwellTracking, getLastUserQuery, setActiveDwellMessage]);

  // This component doesn't render anything
  return null;
}

export default LearningTracker;
